/**
 * usePanelSurface - the React skin over the pure floating-panel reducer.
 *
 * Holds one surface's layout, persists every change, and tracks whether the
 * viewport is wide enough to float panels rather than present them as sheets.
 * All the rules live in `./panelLayout`; this file only turns them into state.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getLayout, setLayout as persistLayout } from '../../../services/layout/layoutStore';
import useLayoutSync from '../../../services/layout/useLayoutSync';
import {
  DockSide,
  PanelLayout,
  PanelSlot,
  PanelSurface,
  activeSheet,
  bringToFront,
  dockHeights,
  dockScrolls,
  dockWidth,
  isSingleColumn,
  movePanel,
  panelZIndex,
  panelsInDock,
  validateLayout,
  readingInset,
  resizeDockSplit,
  resizePanel,
  setPanelVisible,
  togglePanel,
} from './panelLayout';

export interface UsePanelSurfaceResult<Id extends string> {
  layout: PanelLayout<Id>;
  singleColumn: boolean;
  /** Visible panels in a dock, top to bottom. Empty on a narrow viewport. */
  dock: (side: DockSide) => PanelSlot<Id>[];
  scrolls: (side: DockSide) => boolean;
  /** Inset the canvas's content column needs on one side. */
  inset: (side: DockSide) => number;
  /** The single width every panel on a side renders at. */
  width: (side: DockSide) => number;
  /**
   * Each visible pane's share of a dock column, top to bottom. Sums to 1.
   * Even when nobody has dragged a divider there.
   */
  heights: (side: DockSide) => number[];
  zIndexOf: (id: Id) => number;
  /** The single panel a narrow viewport shows, if any. */
  sheet: Id | null;
  resize: (id: Id, width: number) => void;
  /**
   * Drag the divider below the pane at `boundaryIndex` in a dock.
   * `delta` is a fraction of the column - positive grows the pane above.
   */
  resizeSplit: (side: DockSide, boundaryIndex: number, delta: number) => void;
  move: (id: Id, dock: DockSide, index: number) => void;
  focus: (id: Id) => void;
  toggle: (id: Id) => void;
  setVisible: (id: Id, visible: boolean) => void;
}

function viewportWidth(): number {
  return typeof window === 'undefined' ? 1440 : window.innerWidth;
}

export function usePanelSurface<Id extends string>(
  surface: PanelSurface<Id>
): UsePanelSurfaceResult<Id> {
  const [layout, setLayout] = useState<PanelLayout<Id>>(() => getLayout(surface));
  const [width, setWidth] = useState<number>(viewportWidth);

  useEffect(() => {
    const onResize = () => setWidth(viewportWidth());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /**
   * A DIFFERENT surface means a different stored arrangement.
   *
   * The layout is read once, in the `useState` initialiser, which is right
   * for the common case and wrong when the surface identity changes under a
   * mounted component - moving between matters without remounting the page,
   * or the user id arriving after a first-ever login. Without this the second
   * matter would be shown the first one's panes and then SAVE them under its
   * own key, quietly copying one arrangement over another.
   */
  const loaded = useRef(surface);
  useEffect(() => {
    if (loaded.current === surface) return;
    loaded.current = surface;
    setLayout(getLayout(surface));
  }, [surface]);

  /*
    And the same arrangement from the SERVER, once it answers.
    `validateLayout` runs over it exactly as it runs over storage: a payload
    written by another device on an older build is untrusted input.
  */
  useLayoutSync(surface.storageKey, (raw) => setLayout(validateLayout(surface, raw)));

  /**
   * Apply a change to the CURRENT layout, not the one this render captured.
   *
   * THE CLOBBER BUG. Every mutator used to be computed from the `layout` in
   * scope, so two calls in one handler both started from the same snapshot
   * and the second silently discarded the first:
   *
   *     panels.setVisible('document', true);   // opens it
   *     panels.focus('document');              // …from the OLD layout, so
   *                                            //   it is closed again
   *
   * That is exactly what "View in document" did, and why nothing appeared.
   * Taking a reducer instead of a value makes the mutators compose.
   */
  const apply = useCallback((change: (current: PanelLayout<Id>) => PanelLayout<Id>) => {
    setLayout((current) => {
      const next = change(current);
      if (next === current) return current;
      // Through the user-scoped store, so an arrangement belongs to whoever
      // made it - see `services/layout/layoutStore`.
      persistLayout(surface, next);
      return next;
    });
  }, [surface]);

  const singleColumn = isSingleColumn(width);
  const sheet = useMemo(
    () => (singleColumn ? activeSheet(surface, layout) : null),
    [surface, layout, singleColumn]
  );

  return {
    layout,
    singleColumn,
    sheet,
    // On a narrow viewport nothing floats - the sheet is rendered separately.
    dock: useCallback(
      (side: DockSide) => (singleColumn ? [] : panelsInDock(surface, layout, side)),
      [surface, layout, singleColumn]
    ),
    scrolls: useCallback(
      (side: DockSide) => !singleColumn && dockScrolls(surface, layout, side),
      [surface, layout, singleColumn]
    ),
    inset: useCallback(
      (side: DockSide) => (singleColumn ? 0 : readingInset(surface, layout, side)),
      [surface, layout, singleColumn]
    ),
    width: useCallback(
      (side: DockSide) => dockWidth(surface, layout, side),
      [surface, layout]
    ),
    heights: useCallback(
      (side: DockSide) => dockHeights(surface, layout, side),
      [surface, layout]
    ),
    zIndexOf: useCallback((id: Id) => panelZIndex(layout, id), [layout]),
    resize: useCallback(
      (id: Id, next: number) => apply((l) => resizePanel(surface, l, id, next)),
      [surface, apply]
    ),
    // Persists exactly like a width does - through the same `apply`, so the
    // split is written to the surface's storage key on every drag frame.
    resizeSplit: useCallback(
      (side: DockSide, boundaryIndex: number, delta: number) =>
        apply((l) => resizeDockSplit(surface, l, side, boundaryIndex, delta)),
      [surface, apply]
    ),
    move: useCallback(
      (id: Id, side: DockSide, index: number) =>
        apply((l) => movePanel(surface, l, id, side, index)),
      [surface, apply]
    ),
    focus: useCallback((id: Id) => apply((l) => bringToFront(l, id)), [apply]),
    toggle: useCallback((id: Id) => apply((l) => togglePanel(l, id)), [apply]),
    setVisible: useCallback(
      (id: Id, visible: boolean) => apply((l) => setPanelVisible(l, id, visible)),
      [apply]
    ),
  };
}

export default usePanelSurface;
